import {
  Badge,
  Box,
  Flex,
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
  Tooltip,
  type NumberInputProps,
} from '@mantine/core';
import { formatMoney } from 'src/common/currency';
import { MetricHint, type MetricHintKey } from 'src/secret-menu/billing/MetricHint';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

export const formatUnits = (value: number) => value.toLocaleString('en-GB');

/** Revenue in pounds, with pence only when there are some: "£55,000", not "£55,000.00". */
export const formatRevenue = (pence: number | null) => formatMoney((pence ?? 0) / 100, 'GBP');

function signOf(value: number): string {
  if (value > 0) return '+';
  if (value < 0) return '−';
  return '';
}

export const formatSignedPence = (pence: number) =>
  `${signOf(pence)}${formatRevenue(Math.abs(pence))}`;

export const formatSignedUnits = (units: number) =>
  `${signOf(units)}${formatUnits(Math.abs(units))}`;

/** A figure that includes Monument's terms, which are typed in rather than read from our data. */
export function ManualBadge() {
  return (
    <Tooltip
      label="Entered by hand from the Strategic partner button, not read from Afternoon's database"
      withArrow
    >
      <Badge size="xs" variant="light" color="orange">
        Manual
      </Badge>
    </Tooltip>
  );
}

type Tone = 'up' | 'down' | 'neutral';

export const toneOf = (change: number | null): Tone => {
  if (change === null || change === 0) return 'neutral';
  return change > 0 ? 'up' : 'down';
};

const TONE_COLORS: Record<Tone, string> = { up: 'green', down: 'red', neutral: 'dimmed' };

const TABULAR = { fontVariantNumeric: 'tabular-nums' } as const;

/**
 * One headline number, laid out the same on every card: a short name with the date or month it
 * stands at beneath, the figure with its change beside it, an optional split into parts, one
 * line of context, and anything else (such as a bar) at the foot.
 */
export function Stat({
  label,
  caption,
  value,
  change,
  rows,
  detail,
  tone = 'neutral',
  hint,
  badge,
  footer,
  size = 'default',
  aside,
}: {
  label: string;
  /** When the figure stands at: "As of 2 Nov 2026", "Oct 2026", "At full adoption". */
  caption?: string;
  value: string;
  /** A coloured change shown beside the figure, such as <ChangeArrow />. */
  change?: React.ReactNode;
  /** The figure split into its parts, one row each. */
  rows?: { label: string; value: string }[];
  detail?: React.ReactNode;
  tone?: Tone;
  hint: MetricHintKey;
  badge?: React.ReactNode;
  /** Anything shown at the foot of the card, such as a progress bar. */
  footer?: React.ReactNode;
  /** A headline card is roomier with a larger figure, for the numbers that matter most. */
  size?: 'headline' | 'default';
  /** Shown to the right of the figures, such as a small trend chart; below them on narrow screens. */
  aside?: React.ReactNode;
}) {
  const headline = size === 'headline';
  const figures = (
    <Stack gap="xs" h="100%" style={aside ? { flex: '0 0 auto', minWidth: 220 } : undefined}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Stack gap={0}>
          <Group gap={6} wrap="nowrap">
            <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
              {label}
            </Text>
            {badge}
          </Group>
          {caption && (
            <Text size="textXs" c="dimmed">
              {caption}
            </Text>
          )}
        </Stack>
        <MetricHint hint={hint} />
      </Group>
      <Group gap="xs" align="baseline" wrap="nowrap">
        <Text fz={headline ? 40 : 28} fw={700} lh={1.1} style={TABULAR}>
          {value}
        </Text>
        {change}
      </Group>
      {rows && rows.length > 0 && (
        <Stack gap={2}>
          {rows.map(row => (
            <Group key={row.label} justify="space-between" wrap="nowrap" gap="xs">
              <Text size="textSm" c="dimmed">
                {row.label}
              </Text>
              <Text size="textSm" fw={FONT_WEIGHTS.semibold} style={TABULAR}>
                {row.value}
              </Text>
            </Group>
          ))}
        </Stack>
      )}
      {detail && (
        <Text size="textSm" c={TONE_COLORS[tone]}>
          {detail}
        </Text>
      )}
      {footer && <Box mt="auto">{footer}</Box>}
    </Stack>
  );
  return (
    <Paper
      p={headline ? 'lg' : 'md'}
      withBorder
      radius="md"
      h="100%"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {aside ? (
        <Flex direction={{ base: 'column', sm: 'row' }} gap="lg">
          {figures}
          <Box style={{ flex: 1, minWidth: 0 }}>{aside}</Box>
        </Flex>
      ) : (
        figures
      )}
    </Paper>
  );
}

/** "£1,116,000 a year", in bold: the yearly figure under a monthly one. */
export function PerYear({ pence, suffix = '' }: { pence: number; suffix?: string }) {
  return (
    <Text span inherit fw={FONT_WEIGHTS.semibold} c="var(--mantine-color-text)">
      {formatRevenue(pence)} a year{suffix}
    </Text>
  );
}

/**
 * "+46% ↑" in green, "−12% ↓" in red, or "0% →" in grey: how far one figure is from another.
 * `period` names what it is compared with, such as "MoM" for month on month.
 */
export function ChangeArrow({
  current,
  previous,
  period,
}: {
  current: number;
  previous: number;
  period?: string;
}) {
  if (previous === 0) return null;
  const percent = Math.round(((current - previous) / previous) * 100);
  const up = current > previous;
  let text = '0% →';
  let color = 'dimmed';
  if (current !== previous) {
    text = `${up ? '+' : '−'}${Math.abs(percent)}% ${up ? '↑' : '↓'}`;
    color = up ? 'green' : 'red';
  }
  return (
    <Text size="textSm" fw={600} c={color} style={{ whiteSpace: 'nowrap' }}>
      {text}
      {period && (
        <Text span inherit c="dimmed" ml={4}>
          {period}
        </Text>
      )}
    </Text>
  );
}

/** A pound amount held in pence and edited in pounds, for fees and prices. */
export function MoneyInput({
  pence,
  onChange,
  ...props
}: Omit<NumberInputProps, 'value' | 'onChange'> & {
  pence: number;
  onChange: (pence: number) => void;
}) {
  return (
    <NumberInput
      prefix="£"
      thousandSeparator=","
      min={0}
      decimalScale={2}
      {...props}
      value={pence / 100}
      onChange={value => onChange(Math.round(Number(value || 0) * 100))}
    />
  );
}
